// Single source of truth for every user-visible string on the Reports
// dashboard — labels, headings, button text, tooltips, empty/error/loading
// copy, modal text, validation messages. No component should hardcode a
// display string; everything routes through text[language].<key>.
const text = {
  en: {
        // Header
        title: "Reports Analytics Center",
        subtitle: "Generate, analyze, and export HR reports.",

        // Stats
        totalReports: "Total Reports",
        generatedThisMonth: "Generated This Month",
        totalDownloads: "Total Downloads",
        avgGenerationTime: "Avg Generation Time",
        totalEmployees: "Total Employees",
        activeEmployees: "Active Employees",
        avgAttendanceRate: "Avg Attendance Rate",
        recentHires: "Recent Hires",

        // Categories (12 requested + kept extras)
        allReports: "All Reports",
        performance: "Performance Reports",
        payroll: "Payroll Reports",
        attendance: "Attendance Reports",
        recruitment: "Recruitment Reports",
        training: "Training Reports",
        budget: "Budget Reports",
        employee: "Employee Reports",
        leave: "Leave Reports",
        overtime: "Overtime Reports",
        shift: "Shift Reports",
        expenses: "Expenses Reports",
        requests: "Requests Reports",
        audit: "Audit & Activity Reports",
        company: "Company Reports",

        // Category Descriptions
        allReportsDesc: "Complete overview of all available reports",
        performanceDesc: "Employee KPIs and productivity metrics",
        payrollDesc: "Salary, benefits, and financial summaries",
        attendanceDesc: "Leave patterns and attendance analytics",
        recruitmentDesc: "Hiring metrics and talent acquisition",
        trainingDesc: "Learning and development progress",
        budgetDesc: "Financial planning and budget analysis",
        employeeDesc: "Headcount, directory, and workforce composition",
        leaveDesc: "Leave requests, balances, and usage patterns",
        overtimeDesc: "Overtime hours, approvals, and cost impact",
        shiftDesc: "Shift and schedule assignments by location",
        expensesDesc: "Salary advances, reimbursements, and outstanding balances",
        requestsDesc: "All employee requests across the system, unified",
        auditDesc: "Key approvals and account activity trail",
        companyDesc: "Company structure, departments, and configuration",

        // Controls
        searchPlaceholder: "Search reports, tags, or authors...",
        searchReportsPlaceholder: "Search reports...",
        newestFirst: "Newest First",
        oldestFirst: "Oldest First",
        mostDownloaded: "Most Downloaded",
        highestRated: "Highest Rated",
        grid: "Grid",
        list: "List",
        filters: "Filters",
        refresh: "Refresh",
        exportBtn: "Export",
        dateRange: "Date Range",

        // Report Actions
        download: "Download",
        share: "Share",
        view: "View",
        edit: "Edit",
        delete: "Delete",
        preview: "Preview",
        generate: "Generate",
        openCategory: "Open",
        backToDashboard: "Back to Dashboard",
        backToCategory: "Back",

        // Report Details
        author: "Author",
        createdBy: "Created By",
        lastGenerated: "Last Generated",
        fileSize: "File Size",
        format: "Format",
        estimatedReadTime: "Estimated Read Time",
        downloads: "Downloads",
        shares: "Shares",
        rating: "Rating",
        reportsCount: "Reports",

        // Status
        ready: "Ready",
        generating: "Generating",
        error: "Error",
        success: "Success",

        // Email Modal
        shareReport: "Share Report",
        sendReportViaEmail: "Send report via email",
        recipients: "Recipients",
        addRecipient: "Add recipient...",
        subject: "Subject",
        message: "Message",
        addPersonalMessage: "Add a personal message...",
        includeAttachment: "Include report as attachment",
        sendNotification: "Send Notification",
        sendWithAttachment: "Send with Attachment",
        sending: "Sending...",
        cancel: "Cancel",

        // Messages
        reportSentSuccess: "Report sent successfully!",
        reportDownloadSuccess: "Report downloaded successfully!",
        reportGenerateSuccess: "New report generated successfully!",
        errorSendingReport: "Failed to send report via email.",
        errorDownloadingReport: "Failed to download report.",
        errorGeneratingReport: "Failed to generate report.",
        userInfoNotAvailable: "User information not available",
        loading: "Loading...",
        noReportsFound: "No reports found",

        // Time
        min: "min",
        today: "today",
        yesterday: "yesterday",
        daysAgo: "days ago",

        // ==================== KPI Cards ====================
        kpiTotalEmployees: "Total Employees",
        kpiActiveEmployees: "Active Employees",
        kpiAbsentToday: "Absent Today",
        kpiOnLeave: "On Leave",
        kpiTotalOvertimeHours: "Total Overtime Hours",
        kpiPendingRequests: "Pending Requests",
        kpiPayrollCost: "Payroll Cost This Month",
        kpiAttendanceRate: "Attendance Rate",
        kpiSubtitleAllStates: "All employment states",
        kpiSubtitleCurrentlyActive: "Currently active",
        kpiSubtitleToday: "As of today",
        kpiSubtitleThisMonth: "This month",
        kpiSubtitleAwaitingAction: "Awaiting your action",
        kpiSubtitleMonthToDate: "Month to date",

        // ==================== Filters Panel ====================
        filtersTitle: "Filters",
        filtersApply: "Apply",
        filtersReset: "Reset",
        filtersCollapse: "Collapse filters",
        filtersExpand: "Show filters",
        filterDateRange: "Date Range",
        filterSpecificDate: "Specific Date",
        filterDateMode: "Date Mode",
        filterDateModeRange: "Range",
        filterDateModeExact: "Exact Day",
        filterEmployee: "Employee",
        filterDepartment: "Department",
        filterCompany: "Company",
        filterManager: "Manager",
        filterEmploymentStatus: "Employment Status",
        filterAttendanceStatus: "Attendance Status",
        filterLeaveType: "Leave Type",
        filterPayrollMonth: "Payroll Month",
        filterShift: "Shift",
        filterSearch: "Search",
        filterAll: "All",
        filterToday: "Today",
        filterThisWeek: "This Week",
        filterThisMonth: "This Month",
        filterThisQuarter: "This Quarter",
        filterCustomRange: "Custom Range",
        filterFrom: "From",
        filterTo: "To",
        filterModeAll: "All",
        filterModeSpecific: "Specific",
        filterModeMultiple: "Multiple",

        // ==================== Data Table ====================
        tableSearch: "Search table...",
        tableColumns: "Columns",
        tableRowsPerPage: "Rows per page",
        tableNoResults: "No matching rows",
        tableSelectAll: "Select all",
        tableSelected: "{count} selected",
        tableExportSelected: "Export Selected",
        tableExportSelectedExcel: "Excel",
        tableExportSelectedPdf: "PDF",
        tablePage: "Page",
        tableOf: "of",
        tableSortAsc: "Sort ascending",
        tableSortDesc: "Sort descending",
        tableResize: "Drag to resize",

        // ==================== Export Menu ====================
        exportMenuTitle: "Export",
        exportExcel: "Excel (.xlsx)",
        exportPdf: "PDF",
        exportEmail: "Email Report",
        exportSchedule: "Schedule Report",

        // ==================== Schedule Modal ====================
        scheduleTitle: "Schedule Report",
        scheduleFrequency: "Frequency",
        scheduleDaily: "Daily",
        scheduleWeekly: "Weekly",
        scheduleMonthly: "Monthly",
        scheduleCustom: "Custom",
        scheduleDayOfWeek: "Day of Week",
        scheduleDayOfMonth: "Day of Month",
        scheduleTime: "Time",
        scheduleRecipients: "Email Recipients",
        scheduleFormat: "Export Format",
        scheduleRunImmediately: "Also run immediately",
        scheduleCreate: "Create Schedule",
        scheduleCancel: "Cancel",
        scheduleSuccess: "Report scheduled successfully!",
        scheduleError: "Failed to schedule report.",
        scheduleActive: "Active",
        schedulePaused: "Paused",
        scheduleNextRun: "Next Run",
        scheduleLastRun: "Last Run",
        scheduleDelete: "Delete Schedule",
        scheduledReportsTitle: "Scheduled Reports",
        noScheduledReports: "No scheduled reports yet",

        // ==================== Recent Reports ====================
        recentReportsTitle: "Recent Reports",
        recentGeneratedBy: "Generated By",
        recentGeneratedTime: "Generated",
        recentOpenAgain: "Open Again",
        noRecentReports: "No reports generated yet",

        // ==================== Insights ====================
        insightsTitle: "Quick Insights",
        insightTopAttendanceDepartment: "Highest Attendance Department",
        insightTopOvertimeDepartment: "Most Overtime Department",
        insightTopLeaveType: "Most Requested Leave Type",
        insightPayrollGrowth: "Payroll Growth (MoM)",
        insightAttendanceTrend: "Attendance Rate This Month",
        insightEmployeeGrowth: "New Hires This Month",
        insightHiringTrend: "Hiring Trend (MoM)",
        insightTurnoverRate: "Turnover Rate",

        // ==================== Report Preview ====================
        previewTitle: "Report Preview",
        previewSummary: "Summary",
        previewCharts: "Charts",
        previewDataTable: "Detailed Data",
        previewTotals: "Totals",
        previewInsights: "Insights",
        previewBarChart: "Bar Chart",
        previewLineChart: "Line Chart",
        previewPieChart: "Pie Chart",
        previewAreaChart: "Area Chart",
        previewHeatMap: "Heat Map",
        previewTrendOverTime: "Trend Over Time",
        previewDistributionTemplate: "{label} Distribution",
        previewGroups: "groups",
        previewNoChartData: "Not enough data to chart yet",

        // ==================== Empty / Error / Loading States ====================
        emptyStateTitle: "No reports found",
        emptyStateDesc: "Try adjusting your filters, or generate a new report to get started.",
        emptyStateAction: "Generate Report",
        errorStateTitle: "Something went wrong",
        errorStateDesc: "We couldn't load this data. Please check your connection and try again.",
        errorStateRetry: "Retry",
        loadingReports: "Loading reports information...",

        // ==================== Report Content Field Labels ====================
        // Every column header / summary-card label shown in the table and
        // preview comes from here — edit a report's on-screen wording in
        // this one place rather than hunting through table/chart code.
        // (The PDF/Excel FILE content has its own equivalent single file:
        // user-service/src/config/reportLabels.js.)
        fieldLabels: {
          infinityCode: "Employee ID", englishName: "Employee Name", location: "Location",
          employeeType: "Department / Type", hiringDate: "Hiring Date", netSalary: "Net Salary",
          totalWorkDays: "Total Work Days", presentDays: "Present Days", lateDays: "Late Days",
          absentDays: "Absent Days", attendanceRate: "Attendance Rate", punctualityRate: "Punctuality Rate",
          totalOvertimeHours: "Total Overtime Hours", totalOvertimeMinutes: "Total Overtime Minutes",
          avgWorkingHours: "Avg Working Hours", performanceScore: "Performance Score",
          basicSalary: "Basic Salary", totalAllowances: "Total Allowances", totalDeductions: "Total Deductions",
          grossSalary: "Gross Salary", monthlyTax: "Monthly Tax", martyrsFund: "Martyrs' Fund",
          workingDays: "Working Days", absenceDays: "Absence Days", overtimeHours: "Overtime Hours",
          paymentStatus: "Payment Status", totalDays: "Total Days",
          contractCompany: "Contract Company", gender: "Gender", age: "Age", nationality: "Nationality",
          education: "Education", major: "Major", graduationYear: "Graduation Year",
          workingYears: "Working Years", percentageOfBudget: "% of Budget",
          status: "Status", company: "Company", workingCompany: "Working Company",
          employmentType: "Employment Type", manager: "Manager", workEmail: "Work Email", phone: "Phone",
          leaveType: "Leave Type", startDate: "Start Date", endDate: "End Date", days: "Days", approvedBy: "Approved By",
          date: "Date", minutesRequested: "Minutes Requested", hoursRequested: "Hours Requested",
          dayOfWeek: "Day of Week", startTime: "Start Time", endTime: "End Time",
          requestedAmount: "Requested Amount", remainingBalance: "Remaining Balance",
          requestedDate: "Requested Date", requestType: "Request Type",
          activity: "Activity", actor: "Actor", timestamp: "Timestamp",
          department: "Department", headcount: "Headcount", isActive: "Status",
          description: "Description", createdDate: "Created Date", detail: "Detail",
          totalEmployees: "Total Employees", totalRequests: "Total Requests", totalActivities: "Total Activities",
          totalRecords: "Total Records", totalAssignments: "Total Assignments", totalCompanies: "Total Companies",
          totalDepartments: "Total Departments", totalHires: "Total Hires", totalBudget: "Total Budget",
          totalNewHires: "Total New Hires", averageSalary: "Average Salary", highestSalary: "Highest Salary",
          lowestSalary: "Lowest Salary", activeEmployees: "Active Employees", pending: "Pending",
          approved: "Approved", rejected: "Rejected", totalDaysTaken: "Total Days Taken",
          totalHoursValue: "Total Hours", totalRequested: "Total Requested", totalOutstanding: "Total Outstanding",
          dateRange: "Date Range", totalActiveEmployees: "Total Active Employees",
          avgAttendanceRate: "Avg Attendance Rate", avgPunctualityRate: "Avg Punctuality Rate",
          totalBasicSalary: "Total Basic Salary", totalGrossSalary: "Total Gross Salary",
          totalTax: "Total Tax", totalMartyrs: "Total Martyrs' Fund", totalNetSalary: "Total Net Salary",
          avgNetSalary: "Avg Net Salary", totalAttendanceRecords: "Total Attendance Records",
          totalPresentDays: "Total Present Days", totalAbsentDays: "Total Absent Days",
          totalLateDays: "Total Late Days", totalHours: "Total Hours",
        },

        // Report Titles
        reportTitles: {
          "Employee Performance Q4 2025": "Employee Performance Q4 2025",
          "Monthly Payroll Summary - December 2025": "Monthly Payroll Summary - December 2025",
          "Weekly Attendance Analytics Dashboard": "Weekly Attendance Analytics Dashboard",
          "Recruitment & Hiring Analysis - 2025": "Recruitment & Hiring Analysis - 2025",
          "Training & Development Progress Report": "Training & Development Progress Report",
          "Budget Analysis - December 2025": "Budget Analysis - December 2025",
        },

        reportDescriptions: {
          "Comprehensive performance analysis for 32 active employees including attendance patterns, productivity metrics, and departmental distribution.":
            "تحليل شامل للأداء لـ 32 موظف نشط يشمل الحضور والإنتاجية والتوزيع الإداري.",
          "Detailed payroll breakdown for 32 employees with total disbursement of $244,000.":
            "تفصيل الرواتب لـ 32 موظف بإجمالي 244,000 دولار.",
          "Current week attendance analysis covering 64 attendance records with 1 present, 67 absent entries.":
            "تحليل حضور الأسبوع الحالي يغطي 64 سجل حضور مع 1 حاضر و67 غائب.",
          "Recent hiring trends with 5 new employees in the last 3 months. Includes hiring patterns, department distribution, and onboarding metrics.":
            "اتجاهات التوظيف الأخيرة مع 5 موظفين جدد خلال آخر 3 شهور تشمل أنماط التوظيف والتوزيع الإداري ومقاييس الإلحاق.",
          "Comprehensive overview of employee training programs, completion rates, and skill development initiatives for 32 active employees.":
            "نظرة شاملة على برامج تدريب الموظفين ومعدلات الإكمال ومبادرات تطوير المهارات لـ 32 موظف نشط.",
          "Financial planning and budget allocation analysis with total HR budget of $244,000 and department-wise breakdown.":
            "تحليل التخطيط المالي وتوزيع الميزانية بإجمالي ميزانية الموارد البشرية 244,000 دولار والتوزيع حسب الإدارات.",
        },

        authors: {
          "HR Department": "HR Department",
          "Finance Team": "Finance Team",
          "Operations Manager": "Operations Manager",
          "Talent Acquisition": "Talent Acquisition",
          "L&D Department": "L&D Department",
          "Finance Department": "Finance Department",
        },

        tags: {
          Q4: "Q4",
          Performance: "Performance",
          Analytics: "Analytics",
          KPI: "KPI",
          December: "December",
          Salary: "Salary",
          Benefits: "Benefits",
          Finance: "Finance",
          Weekly: "Weekly",
          "Real-time": "Real-time",
          Hiring: "Hiring",
          "2025": "2025",
          Onboarding: "Onboarding",
          Development: "Development",
          Skills: "Skills",
          Learning: "Learning",
          Planning: "Planning",
          Analysis: "Analysis",
        },
      },

      ar: {
        // Header
        title: "مركز تحليلات التقارير",
        subtitle: "إنشاء وتحليل وتصدير تقارير الموارد البشرية.",

        // Stats
        totalReports: "إجمالي التقارير",
        generatedThisMonth: "تم إنشاؤها هذا الشهر",
        totalDownloads: "إجمالي التنزيلات",
        avgGenerationTime: "متوسط وقت الإنشاء",
        totalEmployees: "إجمالي الموظفين",
        activeEmployees: "الموظفون النشطون",
        avgAttendanceRate: "متوسط نسبة الحضور",
        recentHires: "التوظيفات الحديثة",

        // Categories
        allReports: "جميع التقارير",
        performance: "تقارير الأداء",
        payroll: "تقارير الرواتب",
        attendance: "تقارير الحضور",
        recruitment: "تقارير التوظيف",
        training: "تقارير التدريب",
        budget: "تقارير الميزانية",
        employee: "تقارير الموظفين",
        leave: "تقارير الإجازات",
        overtime: "تقارير الوقت الإضافي",
        shift: "تقارير المناوبات",
        expenses: "تقارير المصروفات",
        requests: "تقارير الطلبات",
        audit: "تقارير التدقيق والنشاط",
        company: "تقارير الشركة",

        // Category Descriptions
        allReportsDesc: "نظرة شاملة على جميع التقارير المتاحة",
        performanceDesc: "مؤشرات الأداء الرئيسية وقياسات الإنتاجية",
        payrollDesc: "الرواتب والمزايا والملخصات المالية",
        attendanceDesc: "أنماط الإجازات وتحليلات الحضور",
        recruitmentDesc: "مقاييس التوظيف واكتساب المواهب",
        trainingDesc: "تقدم التعلم والتطوير",
        budgetDesc: "التخطيط المالي وتحليل الميزانية",
        employeeDesc: "العدد الإجمالي ودليل الموظفين وتكوين القوى العاملة",
        leaveDesc: "طلبات الإجازات والأرصدة وأنماط الاستخدام",
        overtimeDesc: "ساعات العمل الإضافي والموافقات والتكلفة",
        shiftDesc: "تعيينات المناوبات والجداول حسب الموقع",
        expensesDesc: "السلف المالية والمبالغ المستردة والأرصدة المستحقة",
        requestsDesc: "جميع طلبات الموظفين في النظام في مكان واحد",
        auditDesc: "سجل الموافقات الرئيسية ونشاط الحسابات",
        companyDesc: "هيكل الشركة والأقسام والإعدادات",

        // Controls
        searchPlaceholder: "البحث في التقارير أو الوسوم أو المؤلفين...",
        searchReportsPlaceholder: "البحث في التقارير...",
        newestFirst: "الأحدث أولاً",
        oldestFirst: "الأقدم أولاً",
        mostDownloaded: "الأكثر تنزيلاً",
        highestRated: "الأعلى تقييماً",
        grid: "شبكة",
        list: "قائمة",
        filters: "الفلاتر",
        refresh: "تحديث",
        exportBtn: "تصدير",
        dateRange: "النطاق الزمني",

        // Report Actions
        download: "تنزيل",
        share: "مشاركة",
        view: "عرض",
        edit: "تعديل",
        delete: "حذف",
        preview: "معاينة",
        generate: "إنشاء",
        openCategory: "فتح",
        backToDashboard: "العودة للوحة التحكم",
        backToCategory: "رجوع",

        // Report Details
        author: "المؤلف",
        createdBy: "أنشئ بواسطة",
        lastGenerated: "آخر إنشاء",
        fileSize: "حجم الملف",
        format: "التنسيق",
        estimatedReadTime: "وقت القراءة المقدر",
        downloads: "التنزيلات",
        shares: "المشاركات",
        rating: "التقييم",
        reportsCount: "تقارير",

        // Status
        ready: "جاهز",
        generating: "جاري الإنشاء",
        error: "خطأ",
        success: "نجاح",

        // Email Modal
        shareReport: "مشاركة التقرير",
        sendReportViaEmail: "إرسال التقرير عبر البريد الإلكتروني",
        recipients: "المستلمون",
        addRecipient: "إضافة مستلم...",
        subject: "الموضوع",
        message: "الرسالة",
        addPersonalMessage: "إضافة رسالة شخصية...",
        includeAttachment: "تضمين التقرير كمرفق",
        sendNotification: "إرسال إشعار",
        sendWithAttachment: "إرسال مع المرفق",
        sending: "جاري الإرسال...",
        cancel: "إلغاء",

        // Messages
        reportSentSuccess: "تم إرسال التقرير بنجاح!",
        reportDownloadSuccess: "تم تنزيل التقرير بنجاح!",
        reportGenerateSuccess: "تم إنشاء تقرير جديد بنجاح!",
        errorSendingReport: "فشل إرسال التقرير عبر البريد الإلكتروني.",
        errorDownloadingReport: "فشل تنزيل التقرير.",
        errorGeneratingReport: "فشل إنشاء التقرير.",
        userInfoNotAvailable: "معلومات المستخدم غير متوفرة",
        loading: "جاري التحميل...",
        noReportsFound: "لم يتم العثور على تقارير",

        // Time
        min: "دقيقة",
        today: "اليوم",
        yesterday: "أمس",
        daysAgo: "أيام مضت",

        // ==================== KPI Cards ====================
        kpiTotalEmployees: "إجمالي الموظفين",
        kpiActiveEmployees: "الموظفون النشطون",
        kpiAbsentToday: "الغائبون اليوم",
        kpiOnLeave: "في إجازة",
        kpiTotalOvertimeHours: "إجمالي ساعات العمل الإضافي",
        kpiPendingRequests: "الطلبات المعلقة",
        kpiPayrollCost: "تكلفة الرواتب هذا الشهر",
        kpiAttendanceRate: "نسبة الحضور",
        kpiSubtitleAllStates: "جميع حالات التوظيف",
        kpiSubtitleCurrentlyActive: "نشط حالياً",
        kpiSubtitleToday: "حتى اليوم",
        kpiSubtitleThisMonth: "هذا الشهر",
        kpiSubtitleAwaitingAction: "بانتظار الإجراء",
        kpiSubtitleMonthToDate: "منذ بداية الشهر",

        // ==================== Filters Panel ====================
        filtersTitle: "الفلاتر",
        filtersApply: "تطبيق",
        filtersReset: "إعادة تعيين",
        filtersCollapse: "طي الفلاتر",
        filtersExpand: "إظهار الفلاتر",
        filterDateRange: "النطاق الزمني",
        filterSpecificDate: "تاريخ محدد",
        filterDateMode: "نوع التاريخ",
        filterDateModeRange: "نطاق",
        filterDateModeExact: "يوم محدد",
        filterEmployee: "الموظف",
        filterDepartment: "القسم",
        filterCompany: "الشركة",
        filterManager: "المدير",
        filterEmploymentStatus: "حالة التوظيف",
        filterAttendanceStatus: "حالة الحضور",
        filterLeaveType: "نوع الإجازة",
        filterPayrollMonth: "شهر الرواتب",
        filterShift: "المناوبة",
        filterSearch: "بحث",
        filterAll: "الكل",
        filterToday: "اليوم",
        filterThisWeek: "هذا الأسبوع",
        filterThisMonth: "هذا الشهر",
        filterThisQuarter: "هذا الربع",
        filterCustomRange: "نطاق مخصص",
        filterFrom: "من",
        filterTo: "إلى",
        filterModeAll: "الكل",
        filterModeSpecific: "محدد",
        filterModeMultiple: "متعدد",

        // ==================== Data Table ====================
        tableSearch: "البحث في الجدول...",
        tableColumns: "الأعمدة",
        tableRowsPerPage: "صفوف لكل صفحة",
        tableNoResults: "لا توجد صفوف مطابقة",
        tableSelectAll: "تحديد الكل",
        tableSelected: "{count} محدد",
        tableExportSelected: "تصدير المحدد",
        tableExportSelectedExcel: "اكسيل",
        tableExportSelectedPdf: "PDF",
        tablePage: "صفحة",
        tableOf: "من",
        tableSortAsc: "ترتيب تصاعدي",
        tableSortDesc: "ترتيب تنازلي",
        tableResize: "اسحب لتغيير الحجم",

        // ==================== Export Menu ====================
        exportMenuTitle: "تصدير",
        exportExcel: "Excel (.xlsx)",
        exportPdf: "PDF",
        exportEmail: "إرسال بالبريد",
        exportSchedule: "جدولة التقرير",

        // ==================== Schedule Modal ====================
        scheduleTitle: "جدولة التقرير",
        scheduleFrequency: "التكرار",
        scheduleDaily: "يومي",
        scheduleWeekly: "أسبوعي",
        scheduleMonthly: "شهري",
        scheduleCustom: "مخصص",
        scheduleDayOfWeek: "يوم الأسبوع",
        scheduleDayOfMonth: "يوم الشهر",
        scheduleTime: "الوقت",
        scheduleRecipients: "مستلمو البريد الإلكتروني",
        scheduleFormat: "صيغة التصدير",
        scheduleRunImmediately: "التشغيل فوراً أيضاً",
        scheduleCreate: "إنشاء الجدولة",
        scheduleCancel: "إلغاء",
        scheduleSuccess: "تم جدولة التقرير بنجاح!",
        scheduleError: "فشلت جدولة التقرير.",
        scheduleActive: "نشط",
        schedulePaused: "متوقف",
        scheduleNextRun: "التشغيل القادم",
        scheduleLastRun: "آخر تشغيل",
        scheduleDelete: "حذف الجدولة",
        scheduledReportsTitle: "التقارير المجدولة",
        noScheduledReports: "لا توجد تقارير مجدولة بعد",

        // ==================== Recent Reports ====================
        recentReportsTitle: "التقارير الأخيرة",
        recentGeneratedBy: "أنشئ بواسطة",
        recentGeneratedTime: "تاريخ الإنشاء",
        recentOpenAgain: "فتح مرة أخرى",
        noRecentReports: "لم يتم إنشاء أي تقارير بعد",

        // ==================== Insights ====================
        insightsTitle: "تحليلات سريعة",
        insightTopAttendanceDepartment: "أعلى قسم من حيث الحضور",
        insightTopOvertimeDepartment: "أعلى قسم من حيث العمل الإضافي",
        insightTopLeaveType: "أكثر أنواع الإجازات طلباً",
        insightPayrollGrowth: "نمو الرواتب (شهرياً)",
        insightAttendanceTrend: "نسبة الحضور هذا الشهر",
        insightEmployeeGrowth: "التعيينات الجديدة هذا الشهر",
        insightHiringTrend: "اتجاه التوظيف (شهرياً)",
        insightTurnoverRate: "معدل دوران الموظفين",

        // ==================== Report Preview ====================
        previewTitle: "معاينة التقرير",
        previewSummary: "الملخص",
        previewCharts: "الرسوم البيانية",
        previewDataTable: "البيانات التفصيلية",
        previewTotals: "الإجماليات",
        previewInsights: "التحليلات",
        previewBarChart: "رسم شريطي",
        previewLineChart: "رسم خطي",
        previewPieChart: "رسم دائري",
        previewAreaChart: "رسم مساحي",
        previewHeatMap: "خريطة حرارية",
        previewTrendOverTime: "الاتجاه عبر الزمن",
        previewDistributionTemplate: "التوزيع حسب {label}",
        previewGroups: "مجموعات",
        previewNoChartData: "لا توجد بيانات كافية للرسم البياني بعد",

        // ==================== Empty / Error / Loading States ====================
        emptyStateTitle: "لم يتم العثور على تقارير",
        emptyStateDesc: "حاول تعديل الفلاتر، أو أنشئ تقريراً جديداً للبدء.",
        emptyStateAction: "إنشاء تقرير",
        errorStateTitle: "حدث خطأ ما",
        errorStateDesc: "تعذر تحميل البيانات. يرجى التحقق من الاتصال والمحاولة مرة أخرى.",
        errorStateRetry: "إعادة المحاولة",
        loadingReports: "جاري تحميل التقارير...",

        fieldLabels: {
          infinityCode: "رقم الموظف", englishName: "اسم الموظف", location: "الموقع",
          employeeType: "القسم / النوع", hiringDate: "تاريخ التعيين", netSalary: "صافي الراتب",
          totalWorkDays: "إجمالي أيام العمل", presentDays: "أيام الحضور", lateDays: "أيام التأخير",
          absentDays: "أيام الغياب", attendanceRate: "نسبة الحضور", punctualityRate: "نسبة الانضباط",
          totalOvertimeHours: "إجمالي ساعات العمل الإضافي", totalOvertimeMinutes: "إجمالي دقائق العمل الإضافي",
          avgWorkingHours: "متوسط ساعات العمل", performanceScore: "درجة الأداء",
          basicSalary: "الراتب الأساسي", totalAllowances: "إجمالي البدلات", totalDeductions: "إجمالي الخصومات",
          grossSalary: "إجمالي الراتب", monthlyTax: "الضريبة الشهرية", martyrsFund: "صندوق الشهداء",
          workingDays: "أيام العمل", absenceDays: "أيام الغياب", overtimeHours: "ساعات العمل الإضافي",
          paymentStatus: "حالة الدفع", totalDays: "إجمالي الأيام",
          contractCompany: "شركة التعاقد", gender: "الجنس", age: "العمر", nationality: "الجنسية",
          education: "المؤهل", major: "التخصص", graduationYear: "سنة التخرج",
          workingYears: "سنوات العمل", percentageOfBudget: "% من الميزانية",
          status: "الحالة", company: "الشركة", workingCompany: "شركة العمل",
          employmentType: "نوع التوظيف", manager: "المدير المباشر", workEmail: "البريد الإلكتروني للعمل", phone: "الهاتف",
          leaveType: "نوع الإجازة", startDate: "تاريخ البداية", endDate: "تاريخ النهاية", days: "الأيام", approvedBy: "تمت الموافقة بواسطة",
          date: "التاريخ", minutesRequested: "الدقائق المطلوبة", hoursRequested: "الساعات المطلوبة",
          dayOfWeek: "يوم الأسبوع", startTime: "وقت البداية", endTime: "وقت النهاية",
          requestedAmount: "المبلغ المطلوب", remainingBalance: "الرصيد المتبقي",
          requestedDate: "تاريخ الطلب", requestType: "نوع الطلب",
          activity: "النشاط", actor: "المسؤول", timestamp: "الوقت",
          department: "القسم", headcount: "عدد الموظفين", isActive: "الحالة",
          description: "الوصف", createdDate: "تاريخ الإنشاء", detail: "التفاصيل",
          totalEmployees: "إجمالي الموظفين", totalRequests: "إجمالي الطلبات", totalActivities: "إجمالي الأنشطة",
          totalRecords: "إجمالي السجلات", totalAssignments: "إجمالي التعيينات", totalCompanies: "إجمالي الشركات",
          totalDepartments: "إجمالي الأقسام", totalHires: "إجمالي التعيينات الجديدة", totalBudget: "إجمالي الميزانية",
          totalNewHires: "إجمالي التعيينات الجديدة", averageSalary: "متوسط الراتب", highestSalary: "أعلى راتب",
          lowestSalary: "أقل راتب", activeEmployees: "الموظفون النشطون", pending: "معلق",
          approved: "موافق عليه", rejected: "مرفوض", totalDaysTaken: "إجمالي الأيام المأخوذة",
          totalHoursValue: "إجمالي الساعات", totalRequested: "إجمالي المطلوب", totalOutstanding: "إجمالي المستحق",
          dateRange: "الفترة الزمنية", totalActiveEmployees: "إجمالي الموظفين النشطين",
          avgAttendanceRate: "متوسط نسبة الحضور", avgPunctualityRate: "متوسط نسبة الانضباط",
          totalBasicSalary: "إجمالي الراتب الأساسي", totalGrossSalary: "إجمالي الرواتب الإجمالية",
          totalTax: "إجمالي الضرائب", totalMartyrs: "إجمالي صندوق الشهداء", totalNetSalary: "إجمالي صافي الراتب",
          avgNetSalary: "متوسط صافي الراتب", totalAttendanceRecords: "إجمالي سجلات الحضور",
          totalPresentDays: "إجمالي أيام الحضور", totalAbsentDays: "إجمالي أيام الغياب",
          totalLateDays: "إجمالي أيام التأخير", totalHours: "إجمالي الساعات",
        },

        reportTitles: {
          "Employee Performance Q4 2025": "أداء الموظفين الربع الرابع 2025",
          "Monthly Payroll Summary - December 2025": " الرواتب الشهري - ديسمبر 2025",
          "Weekly Attendance Analytics Dashboard": " تحليلات الحضور الأسبوعية",
          "Recruitment & Hiring Analysis - 2025": "تحليل التوظيف والتعيين - 2025",
          "Training & Development Progress Report": "تقرير تقدم التدريب والتطوير",
          "Budget Analysis - December 2025": "تحليل الميزانية - ديسمبر 2025",
        },

        reportDescriptions: {
          "Comprehensive performance analysis for 32 active employees including attendance patterns, productivity metrics, and departmental distribution.":
            "تحليل شامل للأداء لـ 32 موظف نشط يشمل الحضور والإنتاجية والتوزيع الإداري.",
          "Detailed payroll breakdown for 32 employees with total disbursement of $244,000.":
            "تفصيل الرواتب لـ 32 موظف بإجمالي 244,000 دولار.",
          "Current week attendance analysis covering 64 attendance records with 1 present, 67 absent entries.":
            "تحليل حضور الأسبوع الحالي يغطي 64 سجل حضور مع 1 حاضر و67 غائب.",
          "Recent hiring trends with 5 new employees in the last 3 months. Includes hiring patterns, department distribution, and onboarding metrics.":
            "اتجاهات التوظيف الأخيرة خلال آخر 3 شهور.",
          "Comprehensive overview of employee training programs, completion rates, and skill development initiatives for 32 active employees.":
            "نظرة شاملة على برامج التدريب وتطوير المهارات.",
          "Financial planning and budget allocation analysis with total HR budget of $244,000 and department-wise breakdown.":
            "تحليل التخطيط المالي وتوزيع الميزانية.",
        },

        authors: {
          "HR Department": "إدارة الموارد البشرية",
          "Finance Team": "فريق المالية",
          "Operations Manager": "مدير العمليات",
          "Talent Acquisition": "اكتساب المواهب",
          "L&D Department": "إدارة التعلم والتطوير",
          "Finance Department": "إدارة المالية",
        },

        tags: {
          Q4: "الربع الرابع",
          Performance: "الأداء",
          Analytics: "التحليلات",
          KPI: "مؤشرات الأداء",
          December: "ديسمبر",
          Salary: "الراتب",
          Benefits: "المزايا",
          Finance: "المالية",
          Weekly: "أسبوعي",
          "Real-time": "فوري",
          Hiring: "التوظيف",
          "2025": "2025",
          Onboarding: "الإلحاق",
          Development: "التطوير",
          Skills: "المهارات",
          Learning: "التعلم",
          Planning: "التخطيط",
          Analysis: "التحليل",
        },
      },
    };

export default text;
